import { estimateChatTokens } from './llmStream.js';

// Every debrief call — Quick, Deep, and each follow-up turn — must ask for the
// SAME num_ctx. Ollama cannot resize a runner's context: a different num_ctx
// tears the runner down and rebuilds it, which for a 24B model is a ~17s stall
// plus a full prompt reprocess. The flow used to send 8192 → 16384 → 32768 and
// so reloaded the model twice before the driver's first follow-up was answered.
// min === max also pins the follow-up: a growing conversation can no longer
// drift num_ctx upward turn over turn and reload again mid-chat.
//
// The value itself is a VRAM limit, not a comfort setting: it must be small
// enough that the model still fits ENTIRELY on the GPU. Measured on two 2080
// Tis with mistral-small3.2:24b, KV cache is 2560 MiB at 16384 and 5120 MiB at
// 32768 — and that extra 2.5 GiB is what pushes it over, to 35/41 layers
// offloaded and 11 tok/s instead of 41/41 and 33 tok/s. A context large enough
// to spill costs far more than the reloads this pinning avoids. If the model
// changes, re-check `offloaded N/N layers to GPU` in the Ollama log before
// raising this. The default is overridable per-deployment via
// OLLAMA_COACH_NUM_CTX (see backend config).
export const DEEP_CHAT_MIN_CONTEXT = 16384;
export const DEEP_CHAT_MAX_CONTEXT = DEEP_CHAT_MIN_CONTEXT;
export const DEEP_CHAT_NUM_PREDICT = 700;

function completePairs(turns) {
  const pairs = [];
  for (let index = 0; index < (turns || []).length - 1; index += 1) {
    const user = turns[index];
    const assistant = turns[index + 1];
    if (user?.role === 'user' && assistant?.role === 'assistant'
        && assistant.status === 'done') {
      pairs.push([
        { role: 'user', content: user.text },
        { role: 'assistant', content: assistant.text },
      ]);
      index += 1;
    }
  }
  return pairs;
}

// Retrieved background reading, fenced so it cannot be mistaken for evidence.
// The fence is explicit rather than implied because the retrieval is semantic
// and therefore sometimes wrong in a confident-sounding way: asked why a flat
// trace in a fast corner is not coasting, the closest passage is the one saying
// flat bottoms ARE coasting, since that is the general rule and the exception
// scores lower.
export function formatGuidance(passages) {
  const items = (passages || []).filter(p => (p?.text || '').trim());
  if (!items.length) return '';
  const body = items.map(p => {
    const cite = [p.source, p.locator].filter(Boolean).join(' ');
    return `--- ${cite}\n${p.text.trim()}`;
  }).join('\n\n');
  return [
    'GENERAL COACHING GUIDANCE — background reading, NOT evidence about this session.',
    'It may explain a concept. It must never be used to state what this driver did,',
    'to introduce a number, or to contradict the session evidence above. Where it',
    'disagrees with the analysis, the analysis is right and this is a general rule',
    'meeting an exception. Cite the source if you draw on it.',
    '',
    body,
  ].join('\n');
}

export function fitDeepChatMessages({
  system,
  evidence,
  headline,
  report,
  turns,
  question,
  guidance = '',
  maxContext = DEEP_CHAT_MAX_CONTEXT,
  numPredict = DEEP_CHAT_NUM_PREDICT,
}) {
  const base = [
    { role: 'system', content: system },
    {
      role: 'user',
      content: `SESSION EVIDENCE PACKET — immutable reference material:\n\n${evidence}`,
    },
    {
      role: 'assistant',
      content: `COMPLETED DEEP ANALYSIS:\n\n${[headline, report].filter(Boolean).join('\n\n')}`,
    },
  ];
  const current = { role: 'user', content: question };
  // After the immutable prefix, so eviction takes it before anything that
  // actually grounds the answer.
  const guidanceBlock = (guidance || '').trim()
    ? [{ role: 'user', content: guidance.trim() }]
    : [];
  // Leave room for one compact correction instruction without evicting the
  // immutable system/evidence/report prefix on the validation retry.
  const tokenBudget = maxContext - numPredict - 768;
  if (estimateChatTokens([...base, current]) > tokenBudget) {
    throw new Error('This analysis is too large to fit safely in the follow-up context.');
  }

  const pairs = completePairs(turns);
  const retained = [];
  for (let index = pairs.length - 1; index >= 0; index -= 1) {
    const candidate = [...base, ...guidanceBlock, ...pairs[index],
      ...retained.flat(), current];
    if (estimateChatTokens(candidate) > tokenBudget) break;
    retained.unshift(pairs[index]);
  }

  // Guidance is the first thing dropped under pressure: a slightly less
  // well-read answer beats one that lost the driver's own conversation.
  let guidanceUsed = guidanceBlock;
  if (guidanceUsed.length
      && estimateChatTokens([...base, ...guidanceUsed, ...retained.flat(), current])
         > tokenBudget) {
    guidanceUsed = [];
  }

  return {
    messages: [...base, ...guidanceUsed, ...retained.flat(), current],
    trimmedPairs: pairs.length - retained.length,
    guidanceDropped: guidanceBlock.length > 0 && guidanceUsed.length === 0,
  };
}

const SESSION_QUESTIONS = [
  'What is the clearest next-session experiment from this analysis?',
  'What does the evidence support most confidently?',
  'What general coaching principle should I take from this session?',
];

// priorityCorners carries EVERY corner the engine measured, ranked, with
// priority_role separating the two the driver is asked to work on from the
// watch list. The report renderer filters on that; this did not, and asked
// "Why is Turn 2 my first priority?" about a corner that was deliberately not
// a priority -- Turn 2 leads the array because it scores highest, but its only
// target was suppressed, so it has no primary_type and no action. The third
// question then offered to explain how the driver would know "the change"
// worked when there was no change, and the model, handed the report plus a
// leading question, would have justified a priority the report never made.
export function suggestedDeepQuestions(meta = {}) {
  const corners = (meta?.priorityCorners || [])
    .filter(c => c.priority_role && c.priority_role !== 'watch');
  const [primary, second] = corners;
  if (!primary) return [...SESSION_QUESTIONS];

  const questions = [`Why is ${primary.label} my first priority?`];
  if (primary.own_reference_mph != null) {
    questions.push(`What was different on my faster passes through ${primary.label}?`);
  }
  // The report deliberately coaches two different changes, so spend the last
  // slot on the second one rather than a third angle on the first.
  if (second) {
    questions.push(`How does ${second.label} relate to ${primary.label}?`);
  }
  questions.push(`How will I know the change in ${primary.label} worked?`);
  questions.push('What general coaching principle applies to this pattern?');
  return questions.slice(0, 3);
}

export function referencedPriorityCorners(text, priorityCorners = []) {
  const source = String(text || '').toLocaleLowerCase();
  return priorityCorners.filter(corner => {
    const label = String(corner.label || '').trim().toLocaleLowerCase();
    return label && source.includes(label);
  }).map(corner => ({ cornerId: corner.corner_id, label: corner.label }));
}
